import { observer } from 'owa-mobx-react';
import { readingPane, skipToMessage } from './MailView.locstring.json';
import loc from 'owa-localize';
import { isFeatureEnabled } from 'owa-feature-flags';
import { ListPane } from 'owa-mail-list-pane-view';
import {
    getSelectedTableView,
    shouldSuppressServerMarkReadOnReplyOrForward,
    getIsSearchTableShown,
} from 'owa-mail-list-store';
import {
    getListViewDimensions,
    setListViewDimension,
    isReadingPanePositionBottom,
    isReadingPanePositionRight,
    isReadingPanePositionOff,
    shouldShowListView,
    shouldShowReadingPane,
    getListViewContainerStyles,
    getListViewPlusCardContainerStyles,
    getReadingPaneContainerStyles,
    shouldShowFolderPane,
    getLeftNavWidth,
} from 'owa-mail-layout';
import ReadingPaneContainer from 'owa-mail-reading-pane-container/lib/components/ReadingPaneContainer';
import { NotificationBarHost } from 'owa-notification-bar';
import { triggerResizeEvent } from 'owa-resize-event';
import { ResizeHandle, ResizeHandleDirection, ResizeHandleScenario } from 'owa-resize-handle';
import folderIdToName from 'owa-session-store/lib/utils/folderIdToName';
import { TabType, getActiveContentTab } from 'owa-tab-store';
import { TabBar } from 'owa-tab-view';
import React from 'react';
import getMailListHeader from 'owa-mail-list-pane-view/lib/components/getMailListHeader';
import getStyleSelectorAsPerUserSettings from 'owa-mail-list-view/lib/utils/getStyleSelectorAsPerUserSettings';
import { SkipLinkRegion } from 'owa-skip-link/lib/components/SkipLinkRegion';
import { NotesPane } from 'owa-notes-components';
import folderStore from 'owa-folders';
import { resetFocus } from 'owa-mail-focus-manager';
import { MailHotkeysMap, setupMailHotkeysMapProperties } from 'owa-mail-hotkeys';
import { getSelectedNode } from 'owa-mail-folder-forest-store';
import { getIsNonMailEntitySearchTab, TabbedSearchPane } from 'owa-tabbed-search';
import { isDumpsterOrDumpsterSearchTable as getIsDumpsterOrDumpsterSearchTable } from '../utils/isDumpsterOrDumpsterSearchTable';
import { lazySetAppSections } from 'owa-accessibility-loop-nav';
import { OutlookTourHost } from 'tour-host';
import { type FolderForestNodeType } from 'owa-favorites-types';
import { isCapabilityEnabled } from 'owa-capabilities';
import { getApplicationSettings } from 'owa-application-settings';
import { bleedThroughCapability } from 'owa-capabilities-definitions/lib/bleedThroughCapability';
import classNames from 'owa-classnames';
import { getIsDarkTheme } from 'owa-fabric-theme';
import getIsLightRPOn from 'owa-light-reading-pane-store/lib/selectors/getIsLightRPOn';
import {
    mailListWithRPRight,
    mailListWithRPHiddenOrBottom,
    mailListWithRPHiddenOrBottomDenseless,
    mailListWithRPHiddenOrBottomDenselessNeutral,
    mailListCardContainerWithRPHiddenOrBottom,
    mailListWithRPRightDenseless,
    mailListCardContainerWithRPHiddenOrBottomDenseless,
    mailListCardContainerWithRPHiddenOrBottomDenselessNeutral,
    mailListLeftPaneVisibleFullBleed,
    mailListLeftPaneVisibleFullBleedDenseless,
    mailListLeftPaneVisibleFullBleedDenselessLeftBorderRounded,
    mailListLeftPaneVisibleFullBleedDenselessRightBorderRounded,
    mailListLeftPaneVisibleFullBleedDenselessNoTopMargin,
    mailListNeutralBorder,
    mailListNeutralTopBorder,
    mailListRefresh,
    mailListRefreshNoLeftBorder,
    bleedthroughBackgroundDark,
    bleedthroughBackgroundLight,
    mailListHide,
    subCommandBarPaneColumn,
    subCommandBarPaneRow,
    subCommandBarPaneCommon,
    subCommandBarPaneCommonRefresh,
    subCommandBarPaneMail,
    rightPane,
    rightPaneDenseless,
    readingPane as styles_readingPane,
    cardAndMLWrapper,
    cardAndMLWrapperBizChatSpacing,
    denselessCompose,
    mailListComposeRightBorderDenseless,
    denselessComposeBleedthrough,
    denselessComposeInnerNeutral,
    denselessComposeInnerNeutralLeftBorderRounded,
    denselessComposeBleedthroughNoLeftBorder,
} from './MailModule.scss';
import type { SearchTableQuery } from 'owa-mail-list-search';
import {
    SearchPlusChatEntryPointContainer,
    isSearchPlusChatFeatureEnabledForSearchSession,
} from 'owa-mail-search-plus-chat';
import { useComputedValue } from 'owa-react-hooks/lib/useComputed';
import shouldRenderCopilotForSearchMailResults from 'owa-mail-list-store/lib/utils/shouldRenderCopilotForSearchMailResults';
import { getCurrentModule } from 'owa-app-module-store';
import { Module } from 'owa-workloads-module';
import { shouldShowBizChatInReadingPane } from 'owa-copilot-chat-drawer-store';
import { BlurredBackgroundView } from 'owa-blurred-view';

const defaultExport: React.FunctionComponent = observer(function MailView() {
    React.useEffect(() => {
        lazySetAppSections.importAndExecute('Mail');
        if (isFeatureEnabled('acct-fullOptionsProjectionPopout')) {
            setupMailHotkeysMapProperties();
        }
    }, []);
    const tableView = getSelectedTableView();
    const hasItemSelected = tableView?.selectedRowKeys && tableView.selectedRowKeys.size > 0;
    const isDarkMode = getIsDarkTheme();
    const isLightRPOn = getIsLightRPOn();
    const isBlurredViewEnabled = isFeatureEnabled('rp-blurred-view');

    const isRPPositionRight = isReadingPanePositionRight();
    const isRPPositionBottom = isReadingPanePositionBottom();
    let showReadingPane = shouldShowReadingPane();
    let showListPane = shouldShowListView();
    const showFolderPane = shouldShowFolderPane();
    const activeTab = getActiveContentTab();
    const isDumpsterOrDumpsterSearchTable = getIsDumpsterOrDumpsterSearchTable();
    const isBleedThroughEnabled = isCapabilityEnabled(bleedThroughCapability);
    const isRPDenselessEnabled = isFeatureEnabled('rp-denseless');

    const oldMailListClasses = classNames(
        isRPPositionRight ? mailListWithRPRight : mailListWithRPHiddenOrBottom,
        mailListRefresh, // Bug154909 -- remove SunValley "hacks" when 1js natively supports SV2.
        isBleedThroughEnabled && showFolderPane && mailListLeftPaneVisibleFullBleed,
        !showListPane && mailListHide,
        getListViewContainerStyles()
    );

    const isSearchTableShown = getIsSearchTableShown();

    const shouldShowSearchPlusChat = useComputedValue(() => {
        if (!tableView || getCurrentModule() !== Module.Mail) {
            return false;
        }
        return (
            isSearchTableShown &&
            shouldRenderCopilotForSearchMailResults(
                (tableView.tableQuery as SearchTableQuery).searchProvider
            ) &&
            isSearchPlusChatFeatureEnabledForSearchSession()
        );
    }, [tableView, isSearchTableShown]);

    const getMailListClasses = React.useCallback(() => {
        // left radius is only applied when folder pane is not expanded
        // or when theme is not bleed through enabled and folder pane is expanded
        const leftRadiusDenselessClass =
            (!showFolderPane || !isBleedThroughEnabled) &&
            !isSearchTableShown &&
            mailListLeftPaneVisibleFullBleedDenselessLeftBorderRounded;

        // right radius is only applied when there is nothing selected or RP is not on the right
        // and is not compose
        const rightRadiusDenselessClass =
            (!hasItemSelected || !isRPPositionRight) &&
            !isSearchTableShown &&
            activeTab?.type != TabType.MailCompose &&
            mailListLeftPaneVisibleFullBleedDenselessRightBorderRounded;

        const rightBorderDenselessMLForComposeClass =
            activeTab?.type === TabType.MailCompose &&
            isRPDenselessEnabled &&
            mailListComposeRightBorderDenseless;

        const noTopMarginClass =
            isRPDenselessEnabled &&
            isSearchTableShown &&
            mailListLeftPaneVisibleFullBleedDenselessNoTopMargin;

        return classNames(
            mailListRefresh,
            rightBorderDenselessMLForComposeClass,
            noTopMarginClass,
            !isBleedThroughEnabled &&
                isRPDenselessEnabled &&
                (!hasItemSelected || !isRPPositionRight
                    ? mailListNeutralBorder
                    : mailListNeutralTopBorder),
            mailListLeftPaneVisibleFullBleedDenseless,
            isRPDenselessEnabled && rightRadiusDenselessClass,
            isRPDenselessEnabled && leftRadiusDenselessClass,
            isRPPositionRight && mailListWithRPRight,
            isRPPositionRight
                ? mailListWithRPRightDenseless
                : isBleedThroughEnabled
                ? mailListWithRPHiddenOrBottomDenseless
                : mailListWithRPHiddenOrBottomDenselessNeutral,
            !isRPDenselessEnabled && !showFolderPane && mailListRefreshNoLeftBorder,
            isRPDenselessEnabled &&
                !showFolderPane &&
                isBleedThroughEnabled &&
                mailListRefreshNoLeftBorder,
            !showListPane && mailListHide,
            isBleedThroughEnabled &&
                (isDarkMode ? bleedthroughBackgroundDark : bleedthroughBackgroundLight),
            getListViewContainerStyles()
        );
    }, [
        showFolderPane,
        isRPDenselessEnabled,
        isBleedThroughEnabled,
        hasItemSelected,
        isRPPositionRight,
        isRPPositionBottom,
        showListPane,
        isDarkMode,
        shouldShowSearchPlusChat,
        activeTab,
        isSearchTableShown,
    ]);

    // Adjust list view & reading pane state for tab view
    if (activeTab && !isDumpsterOrDumpsterSearchTable) {
        if (isRPPositionBottom) {
            // When reading pane is on bottom and current tab is full compose, we need to hide list view
            // to give compose enough space.
            // Here we only check TabType.MailCompose. There are still 2 cases that compose can be show but
            // we don't check them here, because:
            // 1. current tab is Primary tab and we have primary compose. This happens when user is open
            // draft items folder. In that case we still show list view otherwise user can never see list
            // view of draft folder.
            // 2. inline compose. This should never happen when reading pane is on bottom.
            showListPane = showListPane && activeTab.type != TabType.MailCompose;
        } else if (!isRPPositionRight) {
            // When reading pane is off, need to adjust reading pane/list view according to current tab type
            // When active tab is primary tab, show reading pane/list view according to the setting in mailStore
            // Otherwise, always show reading pane since reading pane is the container for all other tabs
            showReadingPane = activeTab.type != TabType.Primary || shouldShowReadingPane();
            showListPane = activeTab.type == TabType.Primary && shouldShowListView();
        }
    }
    // When context menu is opened for a message, do not show reading pane, but show tab bar in list view only if
    // reading pane position is off in user setting. (Bug155397)
    const showTabBarInListView =
        showListPane &&
        !showReadingPane &&
        isReadingPanePositionOff() &&
        !isDumpsterOrDumpsterSearchTable;
    const tabBar = React.useMemo(
        () => (showTabBarInListView ? <TabBar /> : null),
        [showTabBarInListView]
    );
    if (!tableView) {
        return null;
    }
    const tableViewId = tableView.id;
    const styleSelectorAsPerUserSettings = getStyleSelectorAsPerUserSettings(tableViewId);
    const showTabBarInReadingPane = !showTabBarInListView && !isDumpsterOrDumpsterSearchTable;
    const cardAndMLWrapperClasses = classNames(
        cardAndMLWrapper,
        !showListPane && mailListHide,
        getListViewPlusCardContainerStyles(hasItemSelected),
        // Add spacing when BizChat is docked in reading pane and search is NOT shown
        isRPDenselessEnabled &&
            shouldShowBizChatInReadingPane(window) &&
            !isSearchTableShown &&
            cardAndMLWrapperBizChatSpacing
    );
    const subCommandBarPaneClasses = classNames(
        isRPPositionBottom ? subCommandBarPaneColumn : subCommandBarPaneRow,
        subCommandBarPaneCommon,
        subCommandBarPaneCommonRefresh, // Bug154909 -- remove SunValley "hacks" when 1js natively supports SV2.
        subCommandBarPaneMail
    );
    const showNotificationBar = !isRPPositionRight;
    const isSearchTable = getIsSearchTableShown();
    const showPersonaHeader = getSelectedNode().type === 1 && !isSearchTable;
    const showSearchRefinersExtendedHeader = isSearchTable && !showPersonaHeader;
    const shouldMailViewRenderHeader = showSearchRefinersExtendedHeader && isRPPositionRight;
    const isNotesFolder = folderIdToName(tableView?.tableQuery?.folderId ?? '') === 'notes';
    const sourceFolder = tableView && folderStore().folderTable.get(tableView.tableQuery.folderId);
    const showTabbedSearchPane = getIsNonMailEntitySearchTab();

    const mailListClasses = isRPDenselessEnabled ? getMailListClasses() : oldMailListClasses;

    const shouldListPaneRenderHeader = () => {
        if (isSearchTable) {
            if (isFeatureEnabled('sea-searchRibbon')) {
                return true;
            } else {
                return false;
            }
        } else {
            return !shouldMailViewRenderHeader;
        }
    };
    if (
        !!sourceFolder &&
        folderIdToName(sourceFolder.id) === 'notes' &&
        isFeatureEnabled('notes-folder-view')
    ) {
        if (window.performance) {
            window.performance.mark('StartStickyNotesSDKLoad');
        }
        return (
            <NotesPane
                scenario="NotesFolder"
                header={getMailListHeader(tableViewId, undefined) || undefined}
            />
        );
    }
    return (
        <div
            className={
                isRPDenselessEnabled && !isBleedThroughEnabled ? rightPaneDenseless : rightPane
            }
        >
            <div className={subCommandBarPaneClasses}>
                {showTabbedSearchPane ? (
                    <TabbedSearchPane header={undefined} />
                ) : (
                    <>
                        <div className={cardAndMLWrapperClasses}>
                            {shouldShowSearchPlusChat && tableView && (
                                <SearchPlusChatEntryPointContainer
                                    isRPDenselessEnabled={isRPDenselessEnabled}
                                    isDarkMode={isDarkMode}
                                    key="entryPoint_0"
                                    tableView={tableView}
                                    isRPPositionRight={isRPPositionRight}
                                    customClassName={classNames(
                                        isRPDenselessEnabled
                                            ? isRPPositionRight
                                                ? mailListWithRPRightDenseless
                                                : isBleedThroughEnabled
                                                ? mailListCardContainerWithRPHiddenOrBottomDenselessNeutral
                                                : mailListCardContainerWithRPHiddenOrBottomDenseless
                                            : undefined,
                                        !isRPDenselessEnabled
                                            ? isRPPositionRight
                                                ? undefined
                                                : mailListCardContainerWithRPHiddenOrBottom
                                            : undefined
                                    )}
                                />
                            )}
                            <ListPane
                                className={mailListClasses}
                                tableViewId={tableViewId}
                                shouldShowHeader={shouldListPaneRenderHeader()} // ListPane renders a header only if MailView hasn't
                                tabBar={tabBar}
                                styleSelectorAsPerUserSettings={styleSelectorAsPerUserSettings}
                                isBleedThroughEnabled={isBleedThroughEnabled}
                            />
                        </div>
                        {showReadingPane && showListPane && (
                            <ResizeHandle
                                direction={
                                    isRPPositionBottom
                                        ? ResizeHandleDirection.horizontal
                                        : ResizeHandleDirection.vertical
                                }
                                onResized={onRightPaneResized}
                                scenario={ResizeHandleScenario.ReadingPane}
                                onResizeViaKeyboardCompleted={onReadingPaneResizeCompleted}
                            />
                        )}
                        {showReadingPane && !shouldShowBizChatInReadingPane(window) && (
                            <SkipLinkRegion
                                skipLinkName={loc(skipToMessage)}
                                role="main"
                                regionName={loc(readingPane)}
                                className={classNames(
                                    styles_readingPane,
                                    getReadingPaneContainerStyles()
                                )}
                                data-app-section="MailReadCompose"
                            >
                                {isRPDenselessEnabled &&
                                    activeTab?.type === TabType.MailCompose && (
                                        <BlurredBackgroundView
                                            className={
                                                isBleedThroughEnabled
                                                    ? denselessComposeBleedthrough
                                                    : denselessCompose
                                            }
                                            gradientClassName={classNames(
                                                isBleedThroughEnabled
                                                    ? showFolderPane
                                                        ? denselessComposeBleedthrough
                                                        : denselessComposeBleedthroughNoLeftBorder
                                                    : denselessCompose,
                                                !isBleedThroughEnabled &&
                                                    denselessComposeInnerNeutral,
                                                ((!isBleedThroughEnabled && !isRPPositionRight) ||
                                                    !showFolderPane) &&
                                                    denselessComposeInnerNeutralLeftBorderRounded
                                            )}
                                            isDarkTheme={isDarkMode && !isLightRPOn}
                                            isBlurred={isBlurredViewEnabled}
                                        />
                                    )}
                                <ReadingPaneContainer
                                    isDumpsterOrDumpsterSearchTable={
                                        isDumpsterOrDumpsterSearchTable
                                    }
                                    suppressServerMarkReadOnReplyOrForward={shouldSuppressServerMarkReadOnReplyOrForward(
                                        tableView
                                    )}
                                    isNotesFolder={isNotesFolder}
                                />
                                {showTabBarInReadingPane && (
                                    <TabBar isDenseless={isRPDenselessEnabled} />
                                )}
                            </SkipLinkRegion>
                        )}
                    </>
                )}
                {showNotificationBar && (
                    <NotificationBarHost
                        hostId={'MailModuleNotificationBarHost'}
                        onNotificationBlur={onNotificationBlurCallback}
                        positionOffset={'bottom'}
                        leftNavWidth={getLeftNavWidth()}
                        listViewWidth={getListViewDimensions().listViewWidth}
                        isFolderPaneExpanded={showFolderPane}
                    />
                )}
                {getApplicationSettings('TipsTour').enabled && <OutlookTourHost />}
            </div>
            {isFeatureEnabled('acct-fullOptionsProjectionPopout') ? undefined : <MailHotkeysMap />}
        </div>
    );
}, 'MailView');
export default defaultExport;
function onRightPaneResized(listViewDimension: number) {
    const isWidth = isReadingPanePositionRight();
    setListViewDimension(listViewDimension, isWidth);
    triggerResizeEvent();
}

function onReadingPaneResizeCompleted() {
    resetFocus('onReadingPaneResizeCompleted');
}

function onNotificationBlurCallback() {
    resetFocus('MailViewNotificationBarHost');
}
